/**
 * Module-shared FIFO concurrency limiter for file uploads.
 *
 * The upload pipeline ( `POST /file-records` → presigned S3 POST ) is
 * the hottest backend path the app emits in bursts: drop a folder with
 * 100 files and we'd otherwise fire 100 simultaneous network round-
 * trips, two for each file. Worse, multiple surfaces (workspace,
 * canvas, chat) each instantiate `useFileRecordUploadQueue` so per-hook
 * limits don't help when more than one is active.
 *
 * `withUploadSlot` runs at most `MAX_CONCURRENT` async bodies at once
 * across the whole page session, queuing the rest in arrival order.
 * A waiter resumes as soon as a slot frees up; cancellation is handled
 * by callers (the upload bodies themselves observe their abort signal),
 * so an aborted job still releases its slot via the `finally` branch.
 */

const MAX_CONCURRENT = 10;

let active = 0;
const waiters: Array<() => void> = [];

/**
 * Run `fn` once an upload slot is available. Slots are FIFO, so a
 * dropped folder enqueued before another batch finishes its current
 * 10 still drains in submission order.
 */
export async function withUploadSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (active >= MAX_CONCURRENT) {
    await new Promise<void>((resolve) => {
      waiters.push(resolve);
    });
  }
  active += 1;
  try {
    return await fn();
  } finally {
    active -= 1;
    const next = waiters.shift();
    if (next) next();
  }
}
